import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  ProgressBar,
  ProgressBarProps,
  S3ResourceSelector,
  S3ResourceSelectorProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { AppSettings } from "../../../config/AppSettings";
import { getSnapshotPresignedUrl, listSnapshotS3Objects } from "../../../api/snapshot";
import { PresignedUrlRequest, PresignedUrlRequestMethod } from "../../../api/models/snapshotModels";
import { getSnapshotS3BucketUri } from "../../../utils/snapshot/snapshotUtils";

const DOWNLOAD_SUCCESS = "Download finished";
const DOWNLOAD_ERROR = "An error occurred while downloading, please try again";
const KEY_DOES_NOT_EXIST = "This file does not exist, please check the name and try again";

function DownloadSnapshot() {
  const [s3FetchError, setS3FetchError] = useState(null);
  const [resource, setResource] = useState({ uri: "" });

  const [downloadProgressValue, setDownloadProgressValue] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState<ProgressBarProps.Status | undefined>(undefined);
  const [downloadResultText, setDownloadResultText] = useState("");

  function wrapS3FetchWithErrorHandler(callback) {
    return (...args) => {
      setS3FetchError(null);
      // intercept the error to display it and then propagate to the component
      return callback(...args).catch((error) => {
        setS3FetchError(error.message);
        throw error;
      });
    };
  }

  async function fetchS3Objects(_: string, pathPrefix: string): Promise<S3ResourceSelectorProps.Object[]> {
    const listS3ObjectsResponse = await listSnapshotS3Objects({ prefix: pathPrefix });
    if (!listS3ObjectsResponse.s3objects || listS3ObjectsResponse.errorMessage) {
      throw new Error(listS3ObjectsResponse.errorMessage ?? "Fetching S3 objects failed");
    }
    return listS3ObjectsResponse.s3objects.map((s3Object) => ({
      Key: s3Object.key.substring(s3Object.key.indexOf(pathPrefix) + pathPrefix.length),
      LastModified: s3Object.lastModified,
      Size: s3Object.size,
      IsFolder: s3Object.isFolder,
    }));
  }

  function onDownloadStart() {
    setDownloadStatus("in-progress");
    setDownloadResultText("");
    setDownloadProgressValue(0);
  }

  function onDownloadSuccess() {
    setDownloadProgressValue(100);
    setDownloadStatus("success");
    setDownloadResultText(DOWNLOAD_SUCCESS);
  }

  function onDownloadError(error: string) {
    setDownloadStatus("error");
    setDownloadResultText(error);
  }

  async function getPresignedGetUrl() {
    const presignedUrlRequest: PresignedUrlRequest = {
      keyName: resource.uri.substring(getSnapshotS3BucketUri().length),
      method: PresignedUrlRequestMethod.GET,
    };
    const presignedUrlResponse = await getSnapshotPresignedUrl(presignedUrlRequest);
    if (!presignedUrlResponse.keyExists) {
      throw new Error(KEY_DOES_NOT_EXIST);
    } else if (presignedUrlResponse.presignedUrl) {
      return presignedUrlResponse.presignedUrl;
    } else {
      throw new Error(presignedUrlResponse.errorMessage ?? DOWNLOAD_ERROR);
    }
  }

  async function download() {
    onDownloadStart();
    const presignedUrl = await getPresignedGetUrl().catch((err) => onDownloadError(err.toString()));
    if (presignedUrl) {
      window.open(presignedUrl);
      onDownloadSuccess();
    }
  }

  return (
    <SpaceBetween size="xl" direction="vertical">
      <S3ResourceSelector
        resource={resource}
        alert={
          s3FetchError && (
            <SelfDismissibleAlert type="error" header="Data fetching error">
              {s3FetchError}
            </SelfDismissibleAlert>
          )
        }
        fetchBuckets={() =>
          Promise.resolve([
            {
              Name: AppSettings().snapshotBucket,
            },
          ])
        }
        fetchObjects={wrapS3FetchWithErrorHandler(fetchS3Objects)}
        fetchVersions={() => Promise.resolve([])}
        onChange={({ detail }) => setResource(detail.resource)}
        selectableItemsTypes={["objects"]}
        objectsIsItemDisabled={(object) => Boolean(object.IsFolder)}
      />
      <Box float="right">
        <Button
          variant="primary"
          loading={downloadStatus === "in-progress"}
          disabled={!Boolean(resource.uri)}
          onClick={download}
        >
          Download
        </Button>
      </Box>
      {downloadStatus && (
        <ProgressBar status={downloadStatus} value={downloadProgressValue} resultText={downloadResultText} />
      )}
    </SpaceBetween>
  );
}

function SelfDismissibleAlert(props) {
  const [visible, setVisible] = useState(true);
  return visible ? <Alert {...props} dismissible={true} onDismiss={() => setVisible(false)} /> : null;
}

export default DownloadSnapshot;
